import { ChangeDetectionStrategy, Component, computed, inject, input, signal } from '@angular/core';
import {
  CheckpointDetailDto,
  EmailMergeDto,
  InspectionDetailDto,
  InspectionEntryItemDto,
  InspectionEntryService,
  InspectionPdfDetailDto,
  InspectionPdfService,
  RepairTaskItemDto,
  RepairTaskService,
} from '@artemis-software/wr-api';
import { BehaviorSubject, filter, firstValueFrom, throwError, timeout } from 'rxjs';
import { NbDialogService, NbMenuItem, NbMenuService, NbToastrService } from '@nebular/theme';
import {
  RepairTaskGenerateComponent,
} from '@pages/inspection-detail-page-v2/repair-tasks/repair-task-generate/repair-task-generate.component';
import { tryWithLoading } from '@/utils/async-utils';
import { SendEmailComponent } from '@components/dialog/send-email/send-email.component';
import { randomId } from '@/utils/random-utils';
import { RepairTaskStatusTranslationPipe } from '@shared/pipes/repairTaskStatusTranslation.pipe';
import { InspectionContext } from '@pages/inspection-detail-page-v2/inspection.context';
import { catchError } from 'rxjs/operators';
import { GenericDialogComponent } from '@components/dialog/generic-dialog/generic-dialog.component';
import { RepairTaskStatusChangeService } from '@services/repair-task-status-change.service';
import EmailTypeEnum = EmailMergeDto.EmailTypeEnum;
import InspectionPdfTypeEnum = InspectionPdfDetailDto.InspectionPdfTypeEnum;

export type InspectionEntryWithCheckpoint = InspectionEntryItemDto & {
  checkpoint: CheckpointDetailDto,
}

@Component({
  standalone: false,
  selector: 'wr-repair-tasks',
  templateUrl: './repair-tasks.component.html',
  styleUrls: ['./repair-tasks.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RepairTasksComponent {

  readonly inspectionContext = inject(InspectionContext);

  readonly inspectionPdfService = inject(InspectionPdfService);

  private readonly statusChangeService = inject(RepairTaskStatusChangeService);
  private readonly repairTaskService = inject(RepairTaskService);
  private readonly nbToastrService = inject(NbToastrService);
  private readonly inspectionEntryService = inject(InspectionEntryService);
  private readonly dialogService = inject(NbDialogService);
  private readonly nbMenuService = inject(NbMenuService);
  private readonly repairTaskStatusTranslationPipe = inject(RepairTaskStatusTranslationPipe);

  readonly inspection = input.required<InspectionDetailDto>();

  readonly canEdit = computed(() => this.inspection().permissions.canEdit);

  readonly contextMenuTag = randomId();

  readonly textFilter = signal('');

  readonly repairTasksFiltered = computed(() => {
    return this.inspectionContext.repairTasks().filter(repairTask => {
      const filter = this.textFilter();
      if (!filter?.length) {
        return true;
      }
      const contractor = repairTask.contractor?.name?.toLowerCase() ?? '';
      const description = repairTask.description?.toLowerCase() ?? '';
      const title = repairTask.title?.toLowerCase() ?? '';
      return contractor.includes(filter) || description.includes(filter) || title.includes(filter);
    });
  });

  readonly repairTasksGrouped = computed(() => {
    return this.inspectionContext.groupRepairTasksByContractor(this.repairTasksFiltered());
  });

  loading$ = new BehaviorSubject<boolean>(false);
  destroy$ = new BehaviorSubject(undefined);

  constructor() {
    this.nbMenuService.onItemClick().pipe(
      filter(menu => menu.tag === this.contextMenuTag),
    ).subscribe(menu => {
      const { status, items } = menu.item.data;
      this.updateStatusForAll(status, items);
    });
  }

  getStatusContextMenu(items: RepairTaskItemDto[]): NbMenuItem[] {
    return Object.values(RepairTaskItemDto.StatusEnum).map((status) => ({
      title: this.repairTaskStatusTranslationPipe.transform(status),
      data: {
        status: status,
        items: items,
      },
    } as NbMenuItem));
  }

  async editRepairTask(repairTask: RepairTaskItemDto) {
    await this.inspectionContext.editRepairTask(repairTask);
  }

  async addRepairTasks(): Promise<void> {
    try {
      const inspection = this.inspection();
      const inspectionEntries = await firstValueFrom(this.inspectionEntryService.getInspectionEntriesWithActionNecessary(inspection.id));
      const result = await firstValueFrom(this.dialogService.open(RepairTaskGenerateComponent, {
        context: {
          entries: inspectionEntries,
          building: inspection.building,
        },
      }).onClose) as string[];

      if (result) {
        const mergePromises: Promise<RepairTaskItemDto>[] = result.map(async (entryId) =>
          await firstValueFrom(this.repairTaskService.merge({ inspectionEntryId: entryId })),
        );

        await Promise.all(mergePromises);
        if (mergePromises.length > 0) {
          this.nbToastrService.success('Reparaturaufträge wurden erfolgreich erstellt', 'Erstellt');
          this.inspectionContext.reloadRepairTasks();
        }
      }
    } catch (error) {
      console.error(error);
      this.nbToastrService.danger('Reparaturaufträge konnten nicht erstellt werden', 'Fehler');
    }
  }

  async showEmailDialog(event: MouseEvent, items: RepairTaskItemDto[]) {
    event.stopPropagation();

    const confirmationDialogRef = this.dialogService.open(GenericDialogComponent, {
      closeOnBackdropClick: false,
      closeOnEsc: false,
      context: {
        header: 'PDF generieren und E-Mail senden',
        message: 'Möchten Sie wirklich ein PDF generieren und eine E-Mail an den Auftragnehmer senden?',
        status: 'warning',
      },
    });

    const userConfirmed = await firstValueFrom(confirmationDialogRef.onClose);

    if (!userConfirmed) {
      this.nbToastrService.info('Aktion abgebrochen', 'Bestätigung abgelehnt');
      return;
    }

    const contractor = items[0].contractor!;
    await this.generatePdf(items[0]);

    try {
      const repairTasksIds = items.map((item) => item.id);

      const emailContent = await tryWithLoading(this.loading$, async () =>
        await firstValueFrom(this.repairTaskService.generateEmailContent({
          inspectionId: this.inspection().id,
          contractorId: contractor.id,
        })),
      );

      const dialogRef = this.dialogService.open(SendEmailComponent, {
        context: {
          title: 'E-Mail senden',
          recipients: contractor?.email ? [contractor.email] : [],
          message: 'Bist du sicher, dass du eine E-Mail an den Auftragnehmer senden willst?',
          emailMessage: emailContent,
        },
      });

      const message = await firstValueFrom(dialogRef.onClose);
      if (!message) {
        this.nbToastrService.info('Es wurde keine E-Mail versendet', 'E-Mail abgebrochen');
        return;
      }

      message.emailType = EmailTypeEnum.RepairTask;
      message.repairTasksId = repairTasksIds;

      this.repairTaskService.sendRepairTaskEmail(contractor.id, message as EmailMergeDto).subscribe(() => {
        this.nbToastrService.success('E-Mail wurde erfolgreich versendet', 'E-Mail versendet');
      });
    } catch (error) {
      console.error('Failed to generate email content:', error);
    }
  }


  private async updateStatusForAll(status: RepairTaskItemDto.StatusEnum, items: RepairTaskItemDto[]) {
    await tryWithLoading(this.loading$, () => this.statusChangeService.changeStatus(items, status));
    this.inspectionContext.reloadRepairTasks();
  }

  async generatePdf(repairTask: RepairTaskItemDto) {
    await tryWithLoading(this.loading$, async () => {
      await firstValueFrom(
        this.inspectionPdfService.generatePdf({
          inspectionId: this.inspectionContext.inspectionId()!,
          inspectionPdfType: InspectionPdfTypeEnum.RepairTaskShortlist,
          contractorId: repairTask.contractor?.id,
          buildingSections: [],
        }).pipe(
          timeout(30000),
          catchError(error => {
            if (error.name === 'TimeoutError') {
              console.error('Request timed out. Please try again.');
            } else {
              console.error('An error occurred:', error.message);
            }
            return throwError(error);
          }),
        ),
      );
    });
  }
}
